import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip } from "../components/Erklaerung";
import { Ampel, Card, KpiCard } from "../components/ui";
import { RoleName } from "../components/RoleName";
import { useApp } from "../../store/AppStore";
import { useLandscapes } from "../../store/cutoverPlan";
import { landscapeRisk, leverStats } from "../../domain/aggregate";
import { basketAllFue, prognoseJeJahr } from "../../domain/analysis";
import { fueLandscapes, nonFueLandscapes } from "../../data/licensing";
import { daysUntil, formatDateDe, formatEuroMio, formatFue, formatNum, formatTage } from "../../domain/rules";

const KEY_LABEL: Record<string, string> = { username: "Benutzername", personalnummer: "Personalnummer", email: "E-Mail", custom: "freies Feld" };

export function Cockpit() {
  // Cutover-Daten aus dem editierbaren Plan (Reiter Cutover-Gates), nicht der starre RFP-Plan.
  const landscapes = useLandscapes();
  const { ds, kpis, price, ratio, identityKey, identity, consolidatedFue, contracted } = useApp();
  // Zielzustand, wenn ALLE Maßnahmen umgesetzt sind (R4) — dieselbe Zahl wie „Alle zusammen"
  // in der Optimierung (crossSystemSummary().basketAll). Gefahrlos = kpis.green: über Personen
  // gerechnet, nicht Σ safeFue je Maßnahme (das zählte Personen doppelt).
  const potenzialAlle = basketAllFue(ds, ratio);

  const risk = landscapeRisk(ds.persons);
  const gates = landscapes.filter((l) => !l.open)
    .map((l) => ({ l, d: daysUntil(l.cutover) ?? 99999 }))
    .filter((x) => x.d >= 0).sort((a, b) => a.d - b.d);
  const nextGate = gates[0];

  const stats = leverStats(ds.levers, ds.persons).sort((a, b) => b.deltaFue - a.deltaFue).slice(0, 3);
  const leverById = (id: string) => ds.levers.find((l) => l.id === id)!;

  const notConsolidated = identityKey !== "personalnummer";
  // Prognose je Jahr aus der Cutover-Kette — keine Wachstumsfaktoren, nur Cutover-Daten und Personen.
  const prognose = prognoseJeJahr(ds, ratio, landscapes.filter((l) => !l.open), contracted, price);

  return (
    <div>
      <PageHeader route="/" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/vertrag" className="block h-full">
          <KpiCard label="Vertrag" value={formatFue(kpis.entitlement)} tone="brand" big sub="vertraglich zugesichert" />
        </Link>
        <Link to="/berechtigungen" className="block h-full">
          <KpiCard label={`Bedarf (${KEY_LABEL[identityKey]})`} value={formatFue(kpis.ist)} tone="red" big
            sub={notConsolidated ? `konsolidiert (Personalnummer): ${formatFue(consolidatedFue)}` : "konsolidiert"} />
        </Link>
        <Link to="/vertrag" className="block h-full">
          <KpiCard label="Delta" value={`+${formatFue(kpis.delta)}`} sub={`≈ ${formatEuroMio(kpis.deltaEuro)} / Jahr (${formatNum(price)} €/FUE)`} tone="red" big />
        </Link>
        <Link to="/optimierung" className="block h-full">
          <KpiCard
            label={<span className="inline-flex items-center gap-1">Potenzial aller Maßnahmen <InfoTip>Zielzustand, wenn alle Maßnahmen umgesetzt sind (R4): jede Person zählt einmal, keine Addition der Einzelwirkungen. Gefahrlos = Anteil, dessen Berechtigungen im Trace-Zeitraum nie genutzt wurden — ebenfalls über Personen gerechnet.</InfoTip></span>}
            value={`−${formatFue(potenzialAlle)}`}
            sub={`davon gefahrlos −${formatFue(kpis.green)}`}
            tone="green" big
          />
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Link to="/personen" className="block h-full">
          <KpiCard label={`Personen (${KEY_LABEL[identityKey]})`} value={formatNum(identity.recognizedPersons)}
            sub={`aus ${formatNum(ds.accounts.length)} Konten · Schlüssel wählbar`} />
        </Link>
        <Link to="/landschaften" className="block h-full">
          <KpiCard
            label="FUE-relevante Landschaften"
            value={`${fueLandscapes.length} von 10`}
            sub={`${nonFueLandscapes.length} mit eigener Metrik (GSP, HCM)`}
          />
        </Link>
        <Link to={nextGate ? `/gates?ls=${nextGate.l.id}` : "/gates"} className="block h-full">
          <KpiCard label={`Nächstes Gate · ${nextGate?.l.name ?? "—"}`} value={nextGate ? formatTage(nextGate.d) : "—"}
            sub={nextGate ? `Cutover ${formatDateDe(nextGate.l.cutover)}` : ""} tone={nextGate && nextGate.d < 60 ? "red" : "neutral"} />
        </Link>
        <Link to="/optimierung" className="block h-full">
          {/* Vorzeichenbewusst: Fällt der Bedarf nach gefahrlosen Maßnahmen UNTER den Vertrag, ist das
              keine „Lücke, die bleibt", sondern ein Puffer — die Karte muss das sagen. */}
          {kpis.restLuecke > 0 ? (
            <KpiCard label="Restlücke nach gefahrlos" value={formatFue(kpis.restLuecke)} sub="bleibt bestehen" tone="amber" />
          ) : (
            <KpiCard label="Puffer nach gefahrlos" value={formatFue(-kpis.restLuecke)} sub="Vertrag reicht" tone="green" />
          )}
        </Link>
      </div>

      <Card title="Landschaften" subtitle={<span className="inline-flex items-center gap-1">Farbe = Berechtigungsrisiko <InfoTip>Anteil über-berechtigter Personen je Landschaft. Grau: eigene Lizenzmetrik, keine FUE-Bewertung. L11: Zuordnung offen.</InfoTip></span>} className="mt-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {landscapes.map((l) =>
            l.open ? (
              // L11: keine Zeigerhand, kein Link — nur der Hinweis, was offen ist
              <div key={l.id} className="bg-sunken rounded-xl border-soft p-3" style={{ borderWidth: 1, borderStyle: "dashed" }}
                title="Das RFP nennt 11 Landschaften, der Migrationsplan 10 Gruppen — welche Landschaft on-prem bleibt, ist offen.">
                <div className="flex items-center justify-between">
                  <span className="text-strong text-sm font-semibold">L11</span>
                  <HelpCircle size={16} className="text-faint" />
                </div>
                <div className="text-dim mt-1 text-xs">Zuordnung offen — im Termin zu klären</div>
              </div>
            ) : (
              <Link key={l.id} to={`/landschaften?ls=${l.id}`}
                className="bg-card shadow-card block rounded-xl border-soft p-3 hover:border-strong"
                style={{ borderWidth: 1 }}>
                <div className="flex items-center justify-between">
                  <span className="text-strong text-sm font-semibold">{l.name}</span>
                  <Ampel state={risk[l.id]?.ampel ?? "grau"} />
                </div>
                <div className="text-faint text-xs">{l.group} · {l.systems.join(", ")}</div>
                <div className="text-dim mt-2 text-xs">{l.wave} · Cutover {formatDateDe(l.cutover)}</div>
              </Link>
            ),
          )}
        </div>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Top-Hebel" subtitle="größte FUE-Reduktion · effektiv (R3)">
          <div className="space-y-1">
            {stats.map((s) => {
              const lv = leverById(s.id);
              return (
                <Link key={s.id} to={`/optimierung?lever=${s.id}`} className="hover:bg-sunken flex items-center justify-between gap-3 rounded-lg px-2 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm"><RoleName id={lv.roleId} name={lv.roleName} /></div>
                    <div className="text-faint text-xs">{lv.sid} · {lv.authObject} · {formatNum(s.effective)} von {formatNum(s.affected)} wirksam</div>
                  </div>
                  <span className="tnum shrink-0 text-sm font-semibold" style={{ color: "var(--color-amp-green)" }}>−{formatFue(s.deltaFue)}</span>
                </Link>
              );
            })}
          </div>
        </Card>

        <Card title="Bedarf entlang der Cutover-Kette je Jahr" subtitle={<span className="inline-flex items-center gap-1">Stand am 31.12. · Lücke × {formatNum(price)} €/FUE <InfoTip>Kumulierter Bedarf = Summe aller FUE-Cutover bis Jahresende (jede Person zählt einmal, an ihrem frühesten FUE-Cutover, R1). Nach Optimierung = kumuliert minus vermeidbare FUE dieser Systeme. Vertrag = Vertragsgrundlage je Klasse (Einstellungen). Lücke/Puffer = kumuliert minus Vertrag; EUR / Jahr nur bei Lücke. Keine Wachstumsfaktoren — die Kurve folgt dem Cutover-Plan (Reiter Cutover-Gates).</InfoTip></span>}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 font-medium">Jahr</th>
                  <th className="px-2 py-2 text-right font-medium">kumuliert</th>
                  <th className="px-2 py-2 text-right font-medium">nach Optimierung</th>
                  <th className="px-2 py-2 text-right font-medium">Vertrag</th>
                  <th className="px-2 py-2 text-right font-medium">Lücke / Puffer</th>
                  <th className="px-2 py-2 text-right font-medium">EUR / Jahr</th>
                </tr>
              </thead>
              <tbody className="tnum">
                {prognose.map((j) => (
                  <tr key={j.jahr} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                    <td className="text-strong px-2 py-2 font-semibold">{j.jahr} <span className="text-faint text-xs font-normal">{j.gates} Gates</span></td>
                    <td className="text-strong px-2 py-2 text-right font-semibold">{formatFue(j.kumuliert)}</td>
                    <td className="px-2 py-2 text-right" style={{ color: "var(--color-amp-green)" }}>{formatFue(j.nachOptimierung)}</td>
                    <td className="text-dim px-2 py-2 text-right">{formatFue(j.vertrag)}</td>
                    <td className="px-2 py-2 text-right font-semibold" style={{ color: j.delta > 0 ? "var(--color-amp-red)" : "var(--color-amp-green)" }}>
                      {j.delta > 0 ? `Lücke ${formatFue(j.delta)}` : `Puffer ${formatFue(-j.delta)}`}
                    </td>
                    <td className="px-2 py-2 text-right" style={{ color: j.euroJahr > 0 ? "var(--color-amp-red)" : "var(--text-faint)" }}>
                      {j.euroJahr > 0 ? formatEuroMio(j.euroJahr) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
